import pt from '../locales/pt';
import { toNumber } from '../utils/number';

export const DISPLAY_GROUP_ORDER = ['conta', 'corretora', 'crypto', 'ouro', 'imobiliario', 'outro'];

/** Contas à ordem e numerário — usáveis de imediato. */
const LIQUID_CATEGORIES = new Set(['bank_account', 'cash']);

/**
 * @param {string} category
 * @returns {'liquid' | 'invested'}
 */
export function resolveLiquidityLayer(category) {
  if (LIQUID_CATEGORIES.has(category)) return 'liquid';
  return 'invested';
}

/**
 * @param {string} category
 * @returns {'conta' | 'corretora' | 'crypto' | 'ouro' | 'imobiliario' | 'outro'}
 */
export function resolveDisplayGroup(category) {
  return resolveFormTypeFromCategory(category);
}

export function displayGroupLabel(groupId) {
  return pt.accounts.form.typeLabels[groupId] || groupId;
}

/** Default liquidity horizon per form type. Empty string = user must pick. */
export const DEFAULT_LIQUIDITY_HORIZON = {
  conta: '', // depends on liquidityLayer: liquid→immediate, invested→days_1_2
  corretora: 'days_2_5',
  crypto: 'days_1_2',
  ouro: 'weeks',
  imobiliario: 'months',
  outro: '',
};

/** Returns the default horizon for a given form type and liquidity layer. */
export function defaultHorizonForType(formType, liquidityLayer) {
  if (formType === 'conta') {
    return liquidityLayer === 'liquid' ? 'immediate' : 'days_1_2';
  }
  return DEFAULT_LIQUIDITY_HORIZON[formType] || '';
}

export const HORIZON_ORDER = ['immediate', 'days_1_2', 'days_2_5', 'weeks', 'months', 'years_plus'];

export function horizonLabel(horizon) {
  return pt.accounts.liquidity.horizons[horizon] || '';
}

/** Resolve the effective horizon for an account (stored value or category default). */
export function resolveEffectiveHorizon(account) {
  if (account.liquidity_horizon) return account.liquidity_horizon;
  const formType = resolveFormTypeFromCategory(account.category);
  const layer = resolveLiquidityLayer(account.category);
  return defaultHorizonForType(formType, layer) || 'days_2_5';
}

export function liquidityLabel(layer) {
  return layer === 'liquid' ? pt.accounts.liquidity.available : pt.accounts.liquidity.invested;
}

export function resolveCategoryFromForm(formType, liquidityLayer) {
  switch (formType) {
    case 'conta':
      return liquidityLayer === 'invested' ? 'savings_account' : 'bank_account';
    case 'corretora':
      return 'brokerage';
    case 'crypto':
      return 'crypto';
    case 'ouro':
      return 'precious_metal';
    case 'imobiliario':
      return 'real_estate';
    case 'outro':
    default:
      return 'other';
  }
}

export function resolveFormTypeFromCategory(category) {
  if (category === 'crypto') return 'crypto';
  if (category === 'brokerage') return 'corretora';
  if (category === 'precious_metal') return 'ouro';
  if (category === 'real_estate' || category === 'vehicle' || category === 'business') return 'imobiliario';
  if (category === 'bank_account' || category === 'savings_account' || category === 'cash') return 'conta';
  return 'outro';
}

function buildBalanceMap(accountSnapshots = []) {
  const map = new Map();
  for (const row of accountSnapshots) {
    map.set(String(row.account), toNumber(row.balance_eur));
  }
  return map;
}

function sortItemsByBalanceDesc(items) {
  return [...items].sort((a, b) => {
    if (b.balanceEur !== a.balanceEur) return b.balanceEur - a.balanceEur;
    return a.account.name.localeCompare(b.account.name, 'pt');
  });
}

/**
 * @param {{
 *   accounts?: Array<{ id: string|number, name: string, category: string, is_active?: boolean, display_order?: number }>,
 *   accountSnapshots?: Array<{ account: string|number, balance_eur?: number|string }>,
 * }} params
 */
export function buildAccountsPageModel({ accounts = [], accountSnapshots = [] }) {
  const balanceMap = buildBalanceMap(accountSnapshots);
  const active = accounts.filter((account) => account.is_active);

  const groupBuckets = new Map(
    DISPLAY_GROUP_ORDER.map((id) => [id, { id, totalEur: 0, items: [] }]),
  );

  let totalEur = 0;
  let liquidEur = 0;
  let investedEur = 0;

  const horizonTotals = Object.fromEntries(HORIZON_ORDER.map((h) => [h, 0]));

  for (const account of active) {
    const balanceEur = balanceMap.get(String(account.id)) ?? 0;
    const liquidityLayer = resolveLiquidityLayer(account.category);
    const displayGroupId = resolveDisplayGroup(account.category);
    const horizon = resolveEffectiveHorizon(account);

    totalEur += balanceEur;
    if (liquidityLayer === 'liquid') {
      liquidEur += balanceEur;
    } else {
      investedEur += balanceEur;
    }
    if (horizon in horizonTotals) {
      horizonTotals[horizon] += balanceEur;
    }

    const bucket = groupBuckets.get(displayGroupId);
    bucket.totalEur += balanceEur;
    bucket.items.push({
      account,
      balanceEur,
      liquidityLayer,
    });
  }

  const groups = DISPLAY_GROUP_ORDER.map((id) => groupBuckets.get(id))
    .filter((group) => group.items.length > 0)
    .map((group) => ({
      ...group,
      items: sortItemsByBalanceDesc(group.items),
      label: displayGroupLabel(group.id),
    }));

  const liquidPct = totalEur > 0 ? Math.round((liquidEur / totalEur) * 100) : 0;

  const horizonBuckets = HORIZON_ORDER.filter((h) => horizonTotals[h] > 0).map((h) => ({
    id: h,
    label: horizonLabel(h),
    totalEur: horizonTotals[h],
    pct: totalEur > 0 ? (horizonTotals[h] / totalEur) * 100 : 0,
  }));

  return {
    totalEur,
    liquidEur,
    investedEur,
    liquidPct,
    investedPct: totalEur > 0 ? 100 - liquidPct : 0,
    horizonBuckets,
    groups,
  };
}
